import {isAnalysisInputStepType} from 'analysis-tool/AnalysisTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {JSONValue} from 'util/Types';

interface DataFrameRawData {
  __typename: 'DataFrameDomainNode';
  id: string;
  name: string;
}

type RawData = DataFrameRawData | {__typename: Exclude<string, 'DataFrameDomainNode'>};

function isDataFrame(rawData: RawData): rawData is DataFrameRawData {
  return rawData.__typename === 'DataFrameDomainNode';
}

interface AnalysisStep {
  id: string;
  stepType: PipelineStepType;
  config: JSONValue;
  incomingEdges: ReadonlyArray<unknown>;
  outputStepDatas: ReadonlyArray<{rawDatas: ReadonlyArray<RawData>}>;
}

export interface EmbeddedAnalysisInputTable {
  dataFrameId: string;
  name: string;
  stepId: string;
  stepType: PipelineStepType;
  /**
   * The dataset a data catalog dataset step read the table from. The step outputs a copy of the dataset's
   * table, which doesn't belong to the dataset itself.
   */
  datasetId: string | null;
}

function getSourceDatasetId(step: AnalysisStep): string | null {
  if (step.stepType !== PipelineStepType.DATA_CATALOG_DATASET) {
    return null;
  }
  const {config} = step;
  const datasetId =
    config != null && typeof config === 'object' && !Array.isArray(config) ? config.datasetId : null;
  return typeof datasetId === 'string' ? datasetId : null;
}

/**
 * The tables an analysis started from: the data frames output by its input steps that have no incoming
 * edges, such as the template's input tables and the datasets, notebook tables and queries it reads.
 */
export default function getEmbeddedAnalysisInputTables(
  steps: ReadonlyArray<AnalysisStep>
): Array<EmbeddedAnalysisInputTable> {
  return steps
    .filter((step) => isAnalysisInputStepType(step.stepType) && step.incomingEdges.length === 0)
    .flatMap((step) =>
      step.outputStepDatas
        .flatMap((stepData) => stepData.rawDatas)
        .filter((rawData) => isDataFrame(rawData))
        .map(({id, name}) => ({
          dataFrameId: id,
          name,
          stepId: step.id,
          stepType: step.stepType,
          datasetId: getSourceDatasetId(step),
        }))
    );
}
